import { getJson } from "../api";
import { startSelectLoading } from "./selectLoading";

const replaceOptions = (select, options, selectedValue = "") => {
  const fragment = document.createDocumentFragment();
  const placeholder = document.createElement("option");
  placeholder.value = "";
  placeholder.textContent = "wybierz...";
  fragment.appendChild(placeholder);

  options.forEach((item) => {
    const option = document.createElement("option");
    option.value = String(item.id ?? "");
    option.textContent = String(item.text ?? "");
    fragment.appendChild(option);
  });

  select.replaceChildren(fragment);
  const normalizedSelection = String(selectedValue ?? "");
  select.value = Array.from(select.options).some((option) => option.value === normalizedSelection)
    ? normalizedSelection
    : "";
};

const initForm = (form) => {
  if (form.classList.contains("form--readonly")) return false;
  if (form.dataset.schoolClassFormInitialized === "1") {
    return false;
  }

  const optionsUrl = String(form.dataset.schoolClassOptionsUrl ?? "").trim();
  const facilitySelect = form.querySelector('[name="id_szkoly_placowki"]');
  const cycleSelect = form.querySelector('[name="id_cykl"]');
  const openingYearSelect = form.querySelector('[name="id_rok_otwarcia"]');
  if (!optionsUrl || !facilitySelect || !cycleSelect || !openingYearSelect) {
    return false;
  }

  form.dataset.schoolClassFormInitialized = "1";
  const initialCycleId = cycleSelect.value;
  const initialOpeningYearId = openingYearSelect.value;
  let requestNumber = 0;

  const clearDependentSelects = () => {
    replaceOptions(cycleSelect, []);
    replaceOptions(openingYearSelect, []);
    cycleSelect.disabled = true;
    openingYearSelect.disabled = true;
  };

  const loadOptions = async ({ preserveCycle = false, preserveOpeningYear = false } = {}) => {
    const currentRequest = ++requestNumber;
    const finishCycleLoading = startSelectLoading(cycleSelect);
    const finishOpeningYearLoading = startSelectLoading(openingYearSelect);
    const facilityId = String(facilitySelect.value ?? "");
    if (!facilityId) {
      clearDependentSelects();
      finishCycleLoading();
      finishOpeningYearLoading();
      return;
    }

    const selectedCycleId = preserveCycle ? String(cycleSelect.value || initialCycleId) : "";
    const selectedOpeningYearId = preserveOpeningYear ? String(openingYearSelect.value || initialOpeningYearId) : "";
    cycleSelect.disabled = true;
    openingYearSelect.disabled = true;

    const requestUrl = new URL(optionsUrl, window.location.origin);
    requestUrl.searchParams.set("id_szkoly_placowki", facilityId);
    if (selectedCycleId) {
      requestUrl.searchParams.set("id_cykl", selectedCycleId);
    }

    try {
      const payload = await getJson(requestUrl.toString());
      if (currentRequest !== requestNumber) {
        return;
      }

      const cycles = Array.isArray(payload?.cycles) ? payload.cycles : [];
      const resolvedCycleId = String(payload?.selected_cycle_id || selectedCycleId || "");
      replaceOptions(cycleSelect, cycles, resolvedCycleId);
      cycleSelect.disabled = facilitySelect.disabled || cycles.length === 0;

      const classes = Array.isArray(payload?.classes) ? payload.classes : [];
      replaceOptions(openingYearSelect, classes, selectedOpeningYearId);
      openingYearSelect.disabled = cycleSelect.disabled || !cycleSelect.value || classes.length === 0;
    } catch {
      if (currentRequest === requestNumber) {
        clearDependentSelects();
      }
    } finally {
      finishCycleLoading();
      finishOpeningYearLoading();
    }
  };

  facilitySelect.addEventListener("change", () => loadOptions());
  cycleSelect.addEventListener("change", () => loadOptions({ preserveCycle: true }));
  loadOptions({ preserveCycle: true, preserveOpeningYear: true });
  return true;
};

export function initSchoolClassForms(root = document) {
  const forms = [
    ...(root instanceof Element && root.matches("[data-school-class-options-url]") ? [root] : []),
    ...Array.from(root.querySelectorAll?.("[data-school-class-options-url]") ?? []),
  ];

  return forms.reduce((initialized, form) => initForm(form) || initialized, false);
}
